import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { collection, getDocs } from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { PrivateUserInfoType } from '@otw/models'

export const getUserDisplayName = (user: PrivateUserInfoType): string => {
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return fullName || user.displayName || user.email || String(user.id)
}

const normalizeUserEmail = (email?: string | null): string =>
  (email || '').trim().toLowerCase()

/** Find a portal user whose email matches (case-insensitive). */
export const findUserByEmail = (
  users: PrivateUserInfoType[],
  email: string,
): PrivateUserInfoType | undefined => {
  const normalized = normalizeUserEmail(email)
  if (!normalized) return undefined
  return users.find((user) => normalizeUserEmail(user.email) === normalized)
}

/** Sales department staff and owners — eligible for lead/contact/company assignment. */
export const isSalesAssignableUser = (user: PrivateUserInfoType): boolean => {
  if (user.disabled) return false
  if (user.isOwner === true) return true
  return user.department === 'sales'
}

/** Inventory (purchasing) department staff and owners — eligible for inventory lead assignment. */
export const isInventoryAssignableUser = (user: PrivateUserInfoType): boolean => {
  if (user.disabled) return false
  if (user.isOwner === true) return true
  return user.department === 'inventory'
}

type UseUsersOptions = {
  enabled?: boolean
}

export const useUsers = (adminOnly: boolean = false, options?: UseUsersOptions) => {
  const { userInfo } = useAuth()
  const roleAllowed = userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin'
  const queryEnabled = (options?.enabled ?? true) && roleAllowed

  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users', adminOnly],
    queryFn: async () => {

      const usersCollection = collection(db, 'users')
      const snapshot = await getDocs(usersCollection)
      const users = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as PrivateUserInfoType[]

      if (adminOnly) {
        return users.filter(user => user.role === 'admin')
      }

      return users
    },
    enabled: queryEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    users: users || [],
    isLoading,
    isError,
    error,
    refetchUsers: refetch,
  }
}

export const useClientUsers = () => {
  const { userInfo } = useAuth()

  const {
    data: clientUsers,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users', 'clients'],
    queryFn: async () => {
      const usersCollection = collection(db, 'users')
      const snapshot = await getDocs(usersCollection)
      const users = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as PrivateUserInfoType[]

      return users.filter(user => user.role === 'client')
    },
    enabled: userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin',
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    clientUsers: clientUsers || [],
    isLoading,
    isError,
    error,
    refetchClientUsers: refetch,
  }
}

export const useUsersByDepartment = (department: string) => {
  const { userInfo } = useAuth()

  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['users', 'department', department],
    queryFn: async () => {
      const usersCollection = collection(db, 'users')
      const snapshot = await getDocs(usersCollection)
      const users = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as PrivateUserInfoType[]

      return users.filter(user => (user as any).department === department)
    },
    enabled: (userInfo?.role === 'admin' || userInfo?.role === 'babyAdmin') && !!department,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    users: users || [],
    isLoading,
    isError,
    error,
    refetchUsers: refetch,
  }
}

const dedupeAndSort = (
  users: PrivateUserInfoType[],
  predicate: (user: PrivateUserInfoType) => boolean,
): PrivateUserInfoType[] => {
  const seen = new Set<string>()
  const list: PrivateUserInfoType[] = []
  for (const user of users) {
    if (!user?.id || !predicate(user)) continue
    const id = String(user.id)
    if (seen.has(id)) continue
    seen.add(id)
    list.push(user)
  }
  return list.sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)))
}

export const useSalesAssignableUsers = () => {
  const { users, isLoading, isError, error, refetchUsers } = useUsers()

  const assignableUsers = useMemo(
    () => dedupeAndSort(users, isSalesAssignableUser),
    [users],
  )

  return {
    users: assignableUsers,
    isLoading,
    isError,
    error,
    refetchUsers,
  }
}

export const useInventoryAssignableUsers = () => {
  const { users, isLoading, isError, error, refetchUsers } = useUsers()

  const assignableUsers = useMemo(
    () => dedupeAndSort(users, isInventoryAssignableUser),
    [users],
  )

  return {
    users: assignableUsers,
    isLoading,
    isError,
    error,
    refetchUsers,
  }
}
